import { useState } from 'react'
import { useSsoToken } from './useSsoToken'
import { ApiValidationError, createFileNoteCaseNote, createTimeEntry } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import { toUtcDateOnly } from '../utils/formatters'

// The File Note eform's short description field, mirrored from FileNoteForm. A time entry
// description can run longer, so anything past the limit moves into the note's detail body.
const FILE_NOTE_DESCRIPTION_MAX = 300

export interface TimeEntryInput {
  // yyyy-mm-dd, as the native date input produces it.
  date: string
  timeWorked: number
  // Optional for the backend, which uses it to catch a labour code left over from a
  // different work package.
  workPackageId: string
  labourCodeId: string
  billingRoleId: string
  // The status the user picked, as an id from /api/time-entries/statuses. The form labels this
  // control "Billing Status" because MC1 does, but the value is a time entry status and must be
  // sent as `statusId`. `billingStatus` is a separate backend field that carries no status —
  // sending it there books the time and silently never submits it for approval.
  statusId: string
  description: string
  // Also file the description as a claim file note (MSD-355).
  createFileNote: boolean
}

export interface TimeEntrySaved {
  // What went wrong after the time was booked: a submission Replicon rejected, or one whose
  // outcome it never confirmed. The entry is saved either way, so these are shown alongside
  // the confirmation rather than treated as a failed save. Empty on a clean save — the
  // backend no longer passes back the timesheet's own standing messages.
  warnings: string[]
  // The status Replicon actually holds, in words — not the one that was asked for. Submission
  // is asynchronous and seldom settles before the response, so asking for Approved normally
  // reads "Submitting", and nothing reports the outcome afterwards. Null when the backend sent
  // no status, so the UI can say nothing rather than guess.
  statusName: string | null
  fileNoteCreated: boolean
  // The time was booked but the file note failed. Reported separately so a note failure
  // never reads as a failed time entry.
  fileNoteError: string | null
}

/**
 * Books a time entry against the claim, and files the description as a file note when the
 * user asked for one (MSD-355).
 *
 * `save` resolves to true once the time entry itself is booked, whatever the file note did.
 */
export function useCreateTimeEntry(claimNumber: string) {
  const { getToken } = useSsoToken()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<TimeEntrySaved | null>(null)

  const save = async (input: TimeEntryInput): Promise<boolean> => {
    setSaving(true)
    setError(null)
    setSaved(null)
    try {
      const token = await getToken()
      const result = await createTimeEntry(
        claimNumber,
        {
          date: toUtcDateOnly(input.date) as Date,
          timeWorked: input.timeWorked,
          workPackageId: input.workPackageId || null,
          labourCodeId: input.labourCodeId,
          billingRoleId: input.billingRoleId,
          // No billing status is collected: the form's "Billing Status" control carries the time
          // entry status, which goes in statusId below. Left null rather than reused, so a real
          // billing status can be added later without changing what submits.
          billingStatus: null,
          description: input.description,
          // Empty means save it and leave it; an Approved id also submits it for approval.
          statusId: input.statusId || null,
        },
        token
      )

      let fileNoteCreated = false
      let fileNoteError: string | null = null
      if (input.createFileNote) {
        try {
          await createFileNoteCaseNote(
            claimNumber,
            {
              fileNoteDescription: input.description.slice(0, FILE_NOTE_DESCRIPTION_MAX),
              fileNoteDetail: input.description.length > FILE_NOTE_DESCRIPTION_MAX ? input.description : null,
            },
            token
          )
          fileNoteCreated = true
        } catch (err: unknown) {
          console.error('[CreateTimeEntry] Failed to create the file note:', err)
          // The time is already booked and cannot be rolled back, so this is reported
          // alongside the success rather than as a save failure.
          fileNoteError = 'The time entry was saved, but the file note could not be created.'
        }
      }

      setSaved({
        warnings: result.warnings ?? [],
        statusName: result.statusName ?? null,
        fileNoteCreated,
        fileNoteError,
      })
      return true
    } catch (err: unknown) {
      console.error('[CreateTimeEntry] Failed to save the time entry:', err)
      // A rejected entry reports why (labour code not on the claim, hours out of range);
      // retrying the same values can't help, so the backend's reason is what's shown.
      const message =
        err instanceof ApiValidationError
          ? err.messages.join(' ')
          : getApiErrorMessage(err, 'Failed to save the time entry. Please try again.')
      setError(message)
      return false
    } finally {
      setSaving(false)
    }
  }

  // Clears the confirmation so the next entry starts from a blank form.
  const reset = () => {
    setError(null)
    setSaved(null)
  }

  return { saving, error, saved, save, reset }
}
